import React, { useMemo } from "react";
import { Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { BUCKET_LABEL, HELMET_BUCKETS, HELMET_LOCATIONS, impactTimeLabel } from "@utils/helmetDerive.mjs";
import { AlertIcon } from "./AlertIcon";
import type { HelmetImpactRow } from "./types";
import "../../Styles/styles-ResultsTable.css";

// The PDF captures the first <table> on the page, so the export keeps it short (plan rule 12).
const EXPORT_MAX_DAYS = 2;
const EXPORT_MAX_PLAYERS = 8;
const EXPORT_MAX_IMPACTS = 8;
const TAG_COLOR = { low: "green", medium: "gold", high: "red" } as const;

interface GroupStats {
  impacts: number;
  load: number;
  loadPerImpact: number | null;
  linearAcceleration: number | null;
  peakHitSp: number | null;
  alerts: number;
  buckets: Record<string, number>;
  locations: Record<string, number>;
}

export interface DayNode {
  day: string;
  stats: GroupStats;
  players: { playerId: number; stats: GroupStats; impacts: HelmetImpactRow[] }[];
}

interface Row {
  key: string;
  kind: "day" | "player" | "impact" | "stub" | "more";
  label: React.ReactNode;
  stats?: GroupStats;
  impact?: HelmetImpactRow;
  children?: Row[];
}

interface Props {
  tree: DayNode[];
  expandedKeys: string[];
  onExpandedKeysChange: (keys: string[]) => void;
  playerName: (playerId: number) => string;
  /** The Player page: the rows are Date then Time, with no player level. */
  singlePlayer?: boolean;
  exportPreview: boolean;
}

export const dayKey = (day: string) => `d|${day}`;
const playerKey = (day: string, playerId: number) => `p|${day}|${playerId}`;

const format = (value: number | null | undefined) => (value == null ? "—" : Math.round(value).toLocaleString("en-US"));
const format1 = (value: number | null | undefined) => (value == null ? "—" : value.toFixed(1));

function SeverityTags({ stats }: { stats: GroupStats }) {
  return (
    <>
      {HELMET_BUCKETS.filter((b: string) => stats.buckets[b]).map((b: "low" | "medium" | "high") => (
        <Tag key={b} color={TAG_COLOR[b]} title={BUCKET_LABEL[b]}>
          {stats.buckets[b]}
        </Tag>
      ))}
    </>
  );
}

export function HelmetImpactTable({
  tree, expandedKeys, onExpandedKeysChange, playerName, singlePlayer = false, exportPreview,
}: Props) {
  const expanded = useMemo(() => new Set(expandedKeys), [expandedKeys]);

  // Child rows exist only for an open row: a season can hold about 100k impacts. A closed row gets one
  // stub child, so the table still draws its expand button.
  const rows = useMemo<Row[]>(() => {
    const days = exportPreview ? tree.slice(0, EXPORT_MAX_DAYS) : tree;
    return days.map((d) => {
      const dk = dayKey(d.day);
      const dayOpen = exportPreview || expanded.has(dk);
      if (singlePlayer) {
        const impacts = d.players.flatMap((p) => p.impacts);
        const shown = exportPreview ? impacts.slice(0, EXPORT_MAX_IMPACTS) : impacts;
        const impactRows: Row[] = shown.map((impact) => ({
          key: `i|${impact.id}`, kind: "impact", label: impactTimeLabel(impact), impact,
        }));
        if (exportPreview && impacts.length > EXPORT_MAX_IMPACTS) {
          impactRows.push({ key: `${dk}|more`, kind: "more", label: `+ ${impacts.length - EXPORT_MAX_IMPACTS} more impacts` });
        }
        return {
          key: dk,
          kind: "day",
          label: d.day,
          stats: d.stats,
          children: dayOpen ? impactRows : [{ key: `${dk}|stub`, kind: "stub", label: null }],
        };
      }
      const players = exportPreview ? d.players.slice(0, EXPORT_MAX_PLAYERS) : d.players;
      const playerRows: Row[] = players.map((p) => {
        const pk = playerKey(d.day, p.playerId);
        const playerOpen = !exportPreview && expanded.has(pk);
        return {
          key: pk,
          kind: "player",
          label: playerName(p.playerId),
          stats: p.stats,
          children: playerOpen
            ? p.impacts.map((impact) => ({ key: `i|${impact.id}`, kind: "impact", label: impactTimeLabel(impact), impact }))
            : exportPreview
              ? undefined
              : [{ key: `${pk}|stub`, kind: "stub", label: null }],
        };
      });
      if (exportPreview && d.players.length > EXPORT_MAX_PLAYERS) {
        playerRows.push({ key: `${dk}|more`, kind: "more", label: `+ ${d.players.length - EXPORT_MAX_PLAYERS} more players` });
      }
      return {
        key: dk,
        kind: "day",
        label: `${d.day} (${d.players.length})`,
        stats: d.stats,
        children: dayOpen ? playerRows : [{ key: `${dk}|stub`, kind: "stub", label: null }],
      };
    });
  }, [tree, expanded, exportPreview, playerName, singlePlayer]);

  const groupCell = (render: (stats: GroupStats) => React.ReactNode) => (_: unknown, row: Row) =>
    row.stats ? render(row.stats) : null;

  const columns: ColumnsType<Row> = [
    {
      title: singlePlayer ? "Date / Time" : "Date / Player / Time",
      key: "label",
      width: 150,
      className: "helmet-label",
      render: (_, row) => <span title={typeof row.label === "string" ? row.label : undefined}>{row.label}</span>,
    },
    { title: "Impacts", key: "impacts", render: (_, row) => (row.impact ? 1 : groupCell((s) => s.impacts)(_, row)) },
    {
      title: "Impact Load",
      key: "load",
      render: (_, row) => (row.impact ? format(row.impact.load) : groupCell((s) => format(s.load))(_, row)),
    },
    {
      title: "Linear Acc. (g)",
      key: "lin",
      render: (_, row) =>
        row.impact ? format1(row.impact.linear_acceleration) : groupCell((s) => format1(s.linearAcceleration))(_, row),
    },
    {
      title: "Peak HITsp",
      key: "hit",
      render: (_, row) => (row.impact ? format1(row.impact.peak_hit_sp) : groupCell((s) => format1(s.peakHitSp))(_, row)),
    },
    // The five middle columns have no width: the fixed layout shares the spare width of the table between them.
    {
      title: "Severity",
      key: "severity",
      className: "helmet-severity",
      render: (_, row) =>
        row.impact ? (
          <Tag color={TAG_COLOR[row.impact.bucket]}>{BUCKET_LABEL[row.impact.bucket]}</Tag>
        ) : (
          groupCell((s) => <SeverityTags stats={s} />)(_, row)
        ),
    },
    {
      title: "Alerts",
      key: "alerts",
      width: 60,
      render: (_, row) =>
        row.impact ? (
          row.impact.is_alert ? <AlertIcon /> : ""
        ) : (
          groupCell((s) => (s.alerts ? <AlertIcon count={s.alerts} /> : ""))(_, row)
        ),
    },
    {
      title: "Location",
      key: "location",
      children: HELMET_LOCATIONS.map((location: string) => ({
        title: <Tooltip title={location}>{location[0]}</Tooltip>,
        key: `loc-${location}`,
        width: 56,
        render: (_: unknown, row: Row) => {
          if (row.impact) {
            return row.impact.location === location ? <span className="helmet-location-dot" aria-label={location} /> : null;
          }
          if (!row.stats) return null;
          const count = row.stats.locations[location];
          return <span className={count ? undefined : "helmet-zero"}>{count}</span>;
        },
      })),
    },
  ];

  const toggle = (key: string) =>
    onExpandedKeysChange(expanded.has(key) ? expandedKeys.filter((k) => k !== key) : [...expandedKeys, key]);

  // The team-table wrapper gives the table the same style as the GPS tab table.
  return (
    <div className="team-table helmet-table-wrap">
      <Table<Row>
        className="gpsdv-table helmet-table"
        size="small"
        tableLayout="fixed"
        pagination={false}
        columns={columns}
        dataSource={rows}
        locale={{ emptyText: "No impact matches the filters" }}
        expandable={{
          expandedRowKeys: exportPreview ? rows.flatMap((r) => [r.key, ...(r.children ?? []).map((c) => c.key)]) : expandedKeys,
          onExpand: (_, row) => toggle(row.key),
          indentSize: 8,
        }}
        rowClassName={(row) =>
          [
            row.kind === "day" ? "helmet-row-day" : "",
            row.kind === "player" ? "helmet-row-player" : "",
            row.impact?.is_alert ? "helmet-row-alert" : "",
          ].join(" ")
        }
        onRow={(row) => ({
          onClick: () => {
            if (row.kind === "day" || row.kind === "player") toggle(row.key);
          },
        })}
      />
    </div>
  );
}
